import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion'
import { duration, enter, exit } from '@/lib/motion'
import { ParallaxImage } from '@/components/motion/parallax-image'
import { highlights } from '@/constants/home'
import { media } from '@/constants/media'

const SLIDE_DURATION = 6000

const slideIn = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: enter(0, duration.base) },
    exit: { opacity: 0, y: -8, transition: exit() }
}

export const HomeResults: FC = (): ReactNode => {
    const [index, setIndex] = useState(0)
    const [paused, setPaused] = useState(false)
    const sectionRef = useRef<HTMLElement>(null)
    const reducedMotion = usePrefersReducedMotion()

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'center center']
    })

    const topFiller = useTransform(scrollYProgress, [0, 1], [-16, 0])
    const bottomFiller = useTransform(scrollYProgress, [0, 1], [16, 0])

    // Autoplay only when the visitor has not asked for reduced motion; the bars still let them step through by hand.
    useEffect(() => {
        if (paused || reducedMotion) return

        const timer = window.setTimeout(() => {
            setIndex((current) => (current + 1) % highlights.length)
        }, SLIDE_DURATION)

        return () => window.clearTimeout(timer)
    }, [index, paused, reducedMotion])

    const active = highlights[index]

    return (
        <section
            ref={sectionRef}
            id='results'
            aria-label="Results in numbers"
            className='relative z-[2] flex h-svh min-h-[640px] w-full items-end justify-center overflow-clip bg-background'
        >
            <ParallaxImage
                src={media.resultsBackground}
                alt=""
                className='absolute inset-0'
            />

            <motion.div
                aria-hidden='true'
                style={reducedMotion ? undefined : { y: topFiller }}
                className='absolute right-0 top-0 z-[1] h-9 w-1/2 bg-background'
            />

            <motion.div
                aria-hidden='true'
                style={reducedMotion ? undefined : { y: bottomFiller }}
                className='absolute bottom-0 left-0 z-[1] h-9 w-1/2 bg-background'
            />

            <div
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
                className='absolute left-1/2 top-1/2 z-[2] flex aspect-[0.776978] w-[min(359px,calc(100%_-_32px))] -translate-x-1/2 -translate-y-1/2 flex-col items-center overflow-hidden bg-background px-5 pb-[110px] pt-[72px] shadow-floating ring-1 ring-white/[0.06] md:w-[424px] md:pb-[140px] md:pt-[88px]'
            >
                <div className='absolute inset-x-5 top-5 flex gap-2'>
                    {highlights.map((highlight, slide) => (
                        <button
                            key={`${highlight.value}-${highlight.label}`}
                            type='button'
                            onClick={() => setIndex(slide)}
                            aria-label={`Show result ${slide + 1} of ${highlights.length}`}
                            aria-current={slide === index ? 'true' : undefined}
                            className='relative h-3 flex-1 cursor-pointer'
                        >
                            <span className='absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 overflow-hidden rounded-[1px] bg-muted'>
                                <span
                                    key={slide === index ? `active-${index}` : `idle-${slide}`}
                                    className={cn(
                                        'absolute inset-0 origin-left bg-foreground',
                                        slide < index && 'scale-x-100',
                                        slide > index && 'scale-x-0',
                                        slide === index && reducedMotion && 'scale-x-100'
                                    )}
                                    style={slide === index && !reducedMotion ? {
                                        animation: `progress-fill ${SLIDE_DURATION}ms linear forwards`,
                                        animationPlayState: paused ? 'paused' : 'running'
                                    } : undefined}
                                />
                            </span>
                        </button>
                    ))}
                </div>

                <figure className='contents'>
                    <div className='flex w-full flex-1 flex-col items-center justify-center gap-6' aria-live={paused ? 'polite' : 'off'}>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.div
                                key={`result-${index}`}
                                {...slideIn}
                                className='flex flex-col items-center gap-6'
                            >
                                <p className='font-sans text-[88px] uppercase leading-[0.85] text-foreground md:text-[112px]'>
                                    {active.value}
                                </p>

                                <blockquote className='max-w-[320px] text-center font-sans text-[22px] leading-[1.15] text-umber md:text-[26px]'>
                                    {active.text}
                                </blockquote>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <figcaption className='absolute inset-x-0 bottom-5 flex justify-center'>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.span
                                key={`caption-${index}`}
                                {...slideIn}
                                className='flex flex-col items-center gap-2'
                            >
                                <span className='font-sans text-[24px] uppercase leading-[0.9] text-foreground'>
                                    {active.label}
                                </span>

                                <span className='font-serif text-[20px] italic leading-[0.9] text-umber'>
                                    {active.project}
                                </span>
                            </motion.span>
                        </AnimatePresence>
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}
